/**
 * Persistent log of "events generated by THIS device" — used by the
 * public MapPage to apply the viewer-privacy filter:
 *
 *   own events     → always shown
 *   others' events → shown only if far from every own event in
 *                    BOTH space AND time
 *
 * The log holds eventGuid (the cross-reference into the server's
 * /v1/events/recent list) + lat/lon (for spatial distance checks).
 * Capped at MAX_ENTRIES to keep localStorage bounded; the oldest
 * entries are dropped when the cap is hit.
 *
 * Storage: localStorage under one key. JSON-serialised array.
 * Cheap to read on map mount; cheap to append on each event.
 */
const KEY = 'joltstream:my-events:v1';
const MAX_ENTRIES = 5000;

export interface MyEvent {
  eventGuid: string;
  lat: number;
  lon: number;
}

function readAll(): MyEvent[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as MyEvent[]) : [];
  } catch {
    return [];
  }
}

function writeAll(rows: MyEvent[]): void {
  try {
    const trimmed = rows.length > MAX_ENTRIES ? rows.slice(-MAX_ENTRIES) : rows;
    localStorage.setItem(KEY, JSON.stringify(trimmed));
  } catch {
    // private browsing / quota — silent fallback to no log. The map
    // filter will just show no own events; not a correctness bug.
  }
}

export function logMyEvent(e: MyEvent): void {
  const rows = readAll();
  rows.push(e);
  writeAll(rows);
}

export function listMyEvents(): MyEvent[] {
  return readAll();
}

export function ownGuidSet(): Set<string> {
  return new Set(readAll().map((r) => r.eventGuid));
}
